import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import Seo from "../components/Seo";
import { useAuth } from "../hooks/useAuth";
import { PasswordField, PASSWORD_RULE, passwordError } from "../components/PasswordField";

const FIELDS = [
  ["firstName", "First name", "given-name", "text"],
  ["lastName", "Last name", "family-name", "text"],
  ["email", "Email", "email", "email"],
  ["phone", "Mobile number", "tel", "tel"],
  ["address1", "Street address", "address-line1", "text"],
  ["city", "City", "address-level2", "text"],
  ["state", "State (2 letters)", "address-level1", "text"],
  ["postalCode", "ZIP code", "postal-code", "text"],
];

export default function AccountRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ...Object.fromEntries(FIELDS.map(([k]) => [k, ""])),
    password: "",
  });
  const [state, setState] = useState({ busy: false, error: null, pending: null });
  const [pwError, setPwError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const invalid = passwordError(form.password);
    setPwError(invalid);
    if (invalid) return;
    setState({ busy: true, error: null, pending: null });
    try {
      const r = await register({ ...form, state: form.state.toUpperCase() });
      // 202: the account is being set up and the customer signs in separately.
      setState({ busy: false, error: null, pending: r.userMessage });
    } catch (err) {
      const payload = err.payload || {};
      if (payload.code === "registration_pending") {
        setState({ busy: false, error: null, pending: payload.userMessage });
      } else {
        setState({ busy: false, error: payload.userMessage || err.message, pending: null });
      }
    }
  };

  return (
    <>
      <Seo title="Create an account | West Branch Marina" path="/account/register" noindex />
      <div className="mx-auto max-w-2xl px-5 py-28 lg:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-navy">Create an account</h1>
        <p className="mt-3 text-sm text-slate-600">
          Keep your trips, codes and balances together. Prefer not to? You can always{" "}
          <Link to="/boat-rentals/west-branch" data-testid="register-guest-link" className="font-bold text-lake underline">
            book as a guest
          </Link>
          .
        </p>

        <form data-testid="register-form" onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" noValidate>
          {state.error && (
            <p data-testid="register-error" role="alert" aria-live="assertive" className="sm:col-span-2 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {state.error}
            </p>
          )}
          {state.pending && (
            <div data-testid="register-pending" role="status" aria-live="polite" className="sm:col-span-2 rounded-xl border border-lake/40 bg-lake/[0.07] p-4 text-sm text-navy">
              <p>{state.pending}</p>
              <button
                type="button"
                data-testid="register-signin-next"
                onClick={() => navigate(`/account/login?email=${encodeURIComponent(form.email)}`)}
                className="mt-4 inline-flex rounded-full bg-ember px-5 py-2.5 text-xs font-bold text-white hover:bg-ember-hover"
              >
                Go to sign in
              </button>
            </div>
          )}
          {FIELDS.map(([name, label, autoComplete, type]) => (
            <div key={name} className={name === "address1" ? "sm:col-span-2" : undefined}>
              <label htmlFor={`register-${name}`} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                {label}
              </label>
              <input
                id={`register-${name}`}
                data-testid={`register-${name}`}
                type={type}
                autoComplete={autoComplete}
                required
                value={form[name]}
                onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                className="mt-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-lake focus:outline-none focus:ring-2 focus:ring-lake/30"
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <PasswordField
              id="register-password"
              testId="register-password"
              label="Password"
              hint={PASSWORD_RULE}
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              error={pwError}
            />
          </div>
          <button
            type="submit"
            data-testid="register-submit"
            disabled={state.busy}
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            {state.busy ? "Creating your account…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          Already have an account?{" "}
          <Link to="/account/login" data-testid="register-login-link" className="font-bold text-lake underline">
            Sign in
          </Link>
        </p>
      </div>
    </>
  );
}
